import React, { useState, useEffect } from "react";
import {
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { Layers, Ticket, Upload } from "lucide-react";
import { colourStyles } from "../../../utils/customSelectStyle";
import { getAllTicketBySlug, getEventBySlug } from "../../../api";
import apiClient from "../../../api/apiClient";
import * as XLSX from "xlsx";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import PageHeader from "../../../components/common/PageHeader/PageHeader";

interface ExcelRow {
  firstName: string;
  lastName: string;
  emailId: string;
  phoneNumber: string;
  maxCount?: number;
}

interface BookingProgress {
  index: number;
  name: string;
  email: string;
  status: "pending" | "processing" | "success" | "error";
  message?: string;
}

const SellTickets = () => {
  const { isNightMode } = useNightMode();
  const [isLoading, setIsLoading] = useState(false);
  const [ticketList, setTicketList] = useState<any[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [bookingProgress, setBookingProgress] = useState<BookingProgress[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [eventData, setEventData] = useState<any>(null);

  // Fetch tickets on component mount
  useEffect(() => {
    getTicketList();
    getEventData();
  }, []);

  const getEventData = async () => {
    try {
      const storedEvent = localStorage.getItem("eventData");
      if (storedEvent) {
        const parsed = JSON.parse(storedEvent);
        const slugId = parsed.slugId;
        if (slugId) {
          const response = await getEventBySlug(slugId);
          const eventDetails = response?.[0];
          if (eventDetails) {
            setEventData(eventDetails);
          } else {
            // Fallback to localStorage data if API fails
            setEventData(parsed);
          }
        } else {
          setEventData(parsed);
        }
      }
    } catch (error) {
      console.error("Error fetching event data:", error);
      // Fallback to localStorage data if API fails
      const storedEvent = localStorage.getItem("eventData");
      if (storedEvent) {
        const parsed = JSON.parse(storedEvent);
        setEventData(parsed);
      }
    }
  };

  const getTicketList = async () => {
    try {
      setIsLoadingTickets(true);
      const eventData = localStorage.getItem("eventData");
      if (eventData) {
        const eventSlug = JSON.parse(eventData).slugId;
        const response = await getAllTicketBySlug(eventSlug);
        const list = Array.isArray(response) ? response : (response?.data || []);
        setTicketList(list);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTicketList([]);
    } finally {
      setIsLoadingTickets(false);
    }
  };

  // Create ticket options for React Select
  const ticketOptions = (Array.isArray(ticketList) ? ticketList : []).map((ticket: any) => ({
    value: ticket.ticketTypeId,
    label: `${ticket.ticketType}${ticket["Venue.venueName"] ? ` (${ticket["Venue.venueName"]})` : ""}`,
    ticket: ticket,
  }));

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      emailId: "",
      maxCount: 1,
      ticketTypeId: "",
      venueId: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First Name is required"),
      lastName: Yup.string().required("Last Name is required"),
      phoneNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid phone number")
        .required("Phone Number is required"),
      emailId: Yup.string()
        .email("Enter a valid email address")
        .required("Email ID is required"),
      maxCount: Yup.number()
        .min(1, "Minimum is 1")
        .required("Max Count is required"),
      ticketTypeId: Yup.string().required("Ticket selection is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const bookingPayload = createBookingPayload(values);
        // After creating the booking, call sendGroupEmailConfirmation API to send confirmation email.
        // bookingPayload does not return booking number directly. 
        // Since the API response from booking creation is not destructured, extract the booking number from the response here.
        const bookingResponse = await apiClient.post('/v2/booking/create-free-booking-single-group', bookingPayload);
        const bookingNo =
          bookingResponse?.data?.bookingNo ||
          bookingResponse?.data?.data?.bookingNo;

        if (bookingNo) {
          try {
            await apiClient.get(`/booking/send-group-confirmation/${bookingNo}`);
          } catch (emailErr) {
            console.error("Failed to send group confirmation email", emailErr);
          }
        }
        toast.success("Ticket created successfully!");
        resetForm();
      } catch (error: any) {
        console.error("Error creating booking:", error);
        toast.error(error.response?.data?.message || "Failed to create ticket");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const createBookingPayload = (values: any) => {
    const selectedTicketData = ticketList.find((t: any) => t.ticketTypeId === values.ticketTypeId);
    return {
      primaryContactDetails: {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNo: values.phoneNumber,
        email: values.emailId,
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
      },
      attendees: Array.from({ length: values.maxCount - 1 }).map((_, index) => ({
        name: `Attendee ${index + 1} (${values.firstName} ${values.lastName})`,
        email: values.emailId,
        phoneNo: values.phoneNumber,
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        ticketTypeId: values.ticketTypeId,
      })),
      totalTicketCount: values.maxCount,
      totalAmount: 0,
      grandTotalAmount: 0,
      processingFee: 0,
      ticketFee: 0,
      discountAmount: 0,
      eventData: {
        eventId: eventData?.eventId,
        eventName: eventData?.eventName,
        description: eventData?.description || "",
        Country: { currency: eventData?.Country?.currency || "usd" },
        Organizer: { organizerId: eventData?.Organizer?.organizerId || 1 },
        showOtherFees: false,
      },
      venueId: values.venueId || selectedTicketData?.venueId || null,
      ticketList: [{ ticketTypeId: values.ticketTypeId }],
      bookingSlotId: null,
    };
  };

  // Generate and download sample Excel template
  const downloadSampleTemplate = () => {
    // Sample data for the template
    const sampleData = [
      {
        "First Name": "John",
        "Last Name": "Doe",
        "Email": "john.doe@example.com",
        "Phone Number": "1234567890",
        "Max Count": 1,
      },
      {
        "First Name": "Jane",
        "Last Name": "Smith",
        "Email": "jane.smith@example.com",
        "Phone Number": "0987654321",
        "Max Count": 2,
      },
    ];

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // Generate Excel file and download
    XLSX.writeFile(wb, "Bulk_Ticket_Template.xlsx");
    toast.success("Sample template downloaded successfully!");
  };

  // Handle Excel file upload
  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

        // Map Excel columns to our data structure
        const mappedData: ExcelRow[] = jsonData.map((row: any) => ({
          firstName: row["First Name"] || row["firstName"] || row["First Name"] || "",
          lastName: row["Last Name"] || row["lastName"] || row["Last Name"] || "",
          emailId: row["Email"] || row["email"] || row["Email"] || row["Email ID"] || "",
          phoneNumber: String(row["Phone"] || row["phone"] || row["Phone Number"] || row["phoneNumber"] || "").replace(/\D/g, ""),
          maxCount: row["Max Count"] || row["maxCount"] || row["Ticket Count"] || 1,
        }));

        // Validate required fields
        const validData = mappedData.filter(
          (row) => row.firstName && row.lastName && row.emailId && row.phoneNumber
        );

        if (validData.length === 0) {
          toast.error("No valid data found in Excel file. Please check the format.");
          return;
        }

        setExcelData(validData);
        toast.success(`${validData.length} records loaded from Excel`);
      } catch (error) {
        console.error("Error parsing Excel:", error);
        toast.error("Failed to parse Excel file");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Open confirmation modal
  const handleBulkSendClick = () => {
    if (!selectedTicket) {
      toast.error("Please select a ticket");
      return;
    }
    if (excelData.length === 0) {
      toast.error("Please upload an Excel file");
      return;
    }
    setConfirmationModal(true);
  };

  // Send bulk bookings
  const handleSendBookings = async () => {
    setIsSending(true);
    setConfirmationModal(false);
    // Keep bulk modal open to show progress
    setBulkModal(true);

    // Initialize progress tracking
    const progress: BookingProgress[] = excelData.map((row, index) => ({
      index,
      name: `${row.firstName} ${row.lastName}`,
      email: row.emailId,
      status: "pending",
    }));
    setBookingProgress(progress);

    let successCount = 0;
    let errorCount = 0;

    // Process each booking sequentially
    for (let i = 0; i < excelData.length; i++) {
      const row = excelData[i];

      // Update status to processing
      setBookingProgress((prev) =>
        prev.map((p, idx) =>
          idx === i ? { ...p, status: "processing" } : p
        )
      );

      try {
        const bookingPayload = {
          primaryContactDetails: {
            firstName: row.firstName,
            lastName: row.lastName,
            phoneNo: row.phoneNumber,
            email: row.emailId,
            address: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
          },
          attendees: Array.from({ length: (row.maxCount || 1) - 1 }).map((_, index) => ({
            name: `Attendee ${index + 1} (${row.firstName} ${row.lastName})`,
            email: row.emailId,
            phoneNo: row.phoneNumber,
            address: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
            ticketTypeId: selectedTicket.value,
          })),
          totalTicketCount: row.maxCount || 1,
          totalAmount: 0,
          grandTotalAmount: 0,
          processingFee: 0,
          ticketFee: 0,
          discountAmount: 0,
          eventData: {
            eventId: eventData?.eventId,
            eventName: eventData?.eventName,
            description: eventData?.description || "",
            Country: { currency: eventData?.Country?.currency || "usd" },
            Organizer: { organizerId: eventData?.Organizer?.organizerId || 1 },
            showOtherFees: false,
          },
          venueId: selectedTicket.ticket?.venueId || null,
          ticketList: [{ ticketTypeId: selectedTicket.value }],
          bookingSlotId: null,
        };

        // After creating the booking, call sendGroupEmailConfirmation API to send confirmation email.
        // bookingPayload does not return booking number directly. 
        // Since the API response from booking creation is not destructured, extract the booking number from the response here.
        const bookingResponse = await apiClient.post('/v2/booking/create-free-booking-single-group', bookingPayload);
        console.log("bookingResponse", bookingResponse);
        const bookingNo =
          bookingResponse?.data?.bookingNo ||
          bookingResponse?.data?.data?.bookingNo;

        if (bookingNo) {
          try {
            await apiClient.get(`/booking/send-group-confirmation/${bookingNo}`);
          } catch (emailErr) {
            console.error("Failed to send group confirmation email", emailErr);
          }
        }
        successCount++;
        setBookingProgress((prev) =>
          prev.map((p, idx) =>
            idx === i
              ? { ...p, status: "success", message: "Booking created successfully" }
              : p
          )
        );
      } catch (error: any) {
        errorCount++;
        setBookingProgress((prev) =>
          prev.map((p, idx) =>
            idx === i
              ? {
                ...p,
                status: "error",
                message: error.response?.data?.message || "Failed to create booking",
              }
              : p
          )
        );
      }
    }

    setIsSending(false);
    toast.success(`Successfully sent ${successCount} bookings. ${errorCount} failed.`);

    // Reset after 5 seconds to allow user to see final progress
    setTimeout(() => {
      setExcelData([]);
      setSelectedTicket(null);
      setBookingProgress([]);
      setBulkModal(false);
    }, 5000);
  };

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />
      <ToastContainer autoClose={2000} />
      <PageHeader
        title="Sell Tickets"
        subtitle="Manually register attendees, issue complimentary or special group tickets, and upload bulk lists."
        icon={Ticket}
        isLoading={isLoading || isLoadingTickets}
        actions={
          <button type="button" className="contact-action-btn primary" onClick={() => setBulkModal(true)}>
            <Upload size={15} /> Bulk Send
          </button>
        }
      />

      <div className="contact-stats-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TICKET TYPES</div>
            <div className="contact-stat-value">
              {isLoadingTickets ? "..." : ticketOptions.length}{" "}
              <span className="fs-14px fw-normal contact-sub-text">available</span>
            </div>
          </div>
          <div className="contact-stat-icon pink">
            <Ticket size={22} />
          </div>
        </div>
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">ISSUE MODE</div>
            <div className="contact-stat-value" style={{ fontSize: 18 }}>
              Complimentary
            </div>
          </div>
          <div className="contact-stat-icon orange">
            <Layers size={22} />
          </div>
        </div>
      </div>

      <div className="contact-table-card p-3 p-md-4">
        <h5 className="v3-section-head mb-3">Attendee Details</h5>
        <p className="contact-sub-text fs-12px mb-4">
          Fill in the guest, choose a ticket type, and issue a free booking confirmation.
        </p>

        <Form onSubmit={formik.handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <FormGroup className="mb-0">
                <Label for="firstName" className="subuser-stat-label">
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  className="subuser-input"
                  placeholder="Enter first name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.firstName && formik.errors.firstName)}
                />
                <FormFeedback>{formik.errors.firstName}</FormFeedback>
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup className="mb-0">
                <Label for="lastName" className="subuser-stat-label">
                  Last Name *
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  className="subuser-input"
                  placeholder="Enter last name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.lastName && formik.errors.lastName)}
                />
                <FormFeedback>{formik.errors.lastName}</FormFeedback>
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup className="mb-0">
                <Label for="phoneNumber" className="subuser-stat-label">
                  Phone Number *
                </Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  className="subuser-input"
                  placeholder="10-digit phone number"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.phoneNumber && formik.errors.phoneNumber)}
                />
                <FormFeedback>{formik.errors.phoneNumber}</FormFeedback>
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup className="mb-0">
                <Label for="emailId" className="subuser-stat-label">
                  Email *
                </Label>
                <Input
                  id="emailId"
                  name="emailId"
                  type="email"
                  className="subuser-input"
                  placeholder="name@example.com"
                  value={formik.values.emailId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.emailId && formik.errors.emailId)}
                />
                <FormFeedback>{formik.errors.emailId}</FormFeedback>
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup className="mb-0">
                <Label className="subuser-stat-label">Ticket *</Label>
                <Select
                  className="eventox-select"
                  value={ticketOptions.find((opt) => opt.value === formik.values.ticketTypeId) || null}
                  options={ticketOptions}
                  styles={colourStyles}
                  onChange={(option: any) => {
                    formik.setFieldValue("ticketTypeId", option?.value || "");
                    formik.setFieldValue("venueId", option?.ticket?.venueId || "");
                  }}
                  onBlur={() => formik.setFieldTouched("ticketTypeId", true)}
                  placeholder="Select ticket type"
                  isClearable
                  isSearchable
                  isLoading={isLoadingTickets}
                />
                {formik.touched.ticketTypeId && formik.errors.ticketTypeId && (
                  <div className="invalid-feedback d-block">{formik.errors.ticketTypeId}</div>
                )}
              </FormGroup>
            </div>

            <div className="col-md-6">
              <FormGroup className="mb-0">
                <Label for="maxCount" className="subuser-stat-label">
                  Quantity *
                </Label>
                <Input
                  id="maxCount"
                  name="maxCount"
                  type="number"
                  min={1}
                  className="subuser-input"
                  placeholder="1"
                  value={formik.values.maxCount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.maxCount && formik.errors.maxCount)}
                />
                <FormFeedback>{formik.errors.maxCount}</FormFeedback>
              </FormGroup>
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3 mt-4 pt-3 border-top border-white-10">
            <p className="contact-sub-text fs-12px mb-0">
              Issues a complimentary booking and emails confirmation when possible.
            </p>
            <button type="submit" className="subuser-btn-primary" disabled={isLoading}>
              <Ticket size={15} />
              {isLoading ? "Processing..." : "Sell Ticket"}
            </button>
          </div>
        </Form>
      </div>

        {/* Bulk Send Modal */}
        <Modal
          isOpen={bulkModal}
          toggle={() => !isSending && setBulkModal(!bulkModal)}
          centered
          size="lg"
          contentClassName="subuser-modal-content"
        >
          <ModalHeader
            toggle={() => !isSending && setBulkModal(!bulkModal)}
            className="subuser-modal-header"
          >
            <Upload size={18} className="text-pink me-2" />
            Bulk Send Tickets
          </ModalHeader>
          <ModalBody className="p-3 p-sm-4">
            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Select Ticket *</Label>
              <Select
                className="eventox-select"
                value={selectedTicket}
                options={ticketOptions}
                styles={colourStyles}
                onChange={(option: any) => setSelectedTicket(option)}
                placeholder="Select Ticket"
                isClearable
                isSearchable
                isLoading={isLoadingTickets}
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Upload Excel File *</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                className="subuser-input"
                onChange={handleExcelUpload}
              />
              <small className="contact-sub-text d-block mt-2 fs-12px">
                Columns: First Name, Last Name, Email, Phone Number (optional: Max Count)
              </small>
              <button
                type="button"
                className="v3-btn-ghost mt-2"
                onClick={downloadSampleTemplate}
              >
                <i className="fa-solid fa-download"></i>
                Download sample Excel template
              </button>
            </FormGroup>

            {excelData.length > 0 && (
              <div className="mb-3 p-3 rounded-3 border border-white-10">
                <span className="fs-13px text-success">
                  <i className="fa-solid fa-check-circle me-2"></i>
                  {excelData.length} records loaded
                </span>
              </div>
            )}

            {(isSending || bookingProgress.length > 0) && bookingProgress.length > 0 && (
              <div className="mt-3">
                <h6 className="contact-name-text fs-13px mb-3">
                  Progress ({bookingProgress.filter((p) => p.status === "success").length}/
                  {bookingProgress.length})
                </h6>
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                  {bookingProgress.map((item, index) => (
                    <div key={index} className="mb-2 p-3 rounded-3 border border-white-10">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-medium contact-name-text fs-13px">{item.name}</span>
                        <span
                          className={`badge ${
                            item.status === "success"
                              ? "bg-success"
                              : item.status === "error"
                                ? "bg-danger"
                                : item.status === "processing"
                                  ? "bg-warning text-dark"
                                  : "bg-secondary"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                      <small className="contact-sub-text d-block">{item.email}</small>
                      {item.message && (
                        <small
                          className={`d-block mt-1 ${
                            item.status === "error" ? "text-danger" : "text-success"
                          }`}
                        >
                          {item.message}
                        </small>
                      )}
                      {item.status === "processing" && (
                        <Progress value={100} animated className="mt-2" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="subuser-modal-footer gap-2">
            <button
              type="button"
              className="subuser-btn-secondary"
              onClick={() => {
                if (!isSending) {
                  setBulkModal(false);
                  setExcelData([]);
                  setSelectedTicket(null);
                  setBookingProgress([]);
                }
              }}
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Cancel"}
            </button>
            {!isSending && (
              <button
                type="button"
                className="subuser-btn-primary"
                onClick={handleBulkSendClick}
                disabled={!selectedTicket || excelData.length === 0}
              >
                <Upload size={14} />
                Send Tickets
              </button>
            )}
          </ModalFooter>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          isOpen={confirmationModal}
          toggle={() => setConfirmationModal(!confirmationModal)}
          centered
          size="lg"
          contentClassName="subuser-modal-content"
        >
          <ModalHeader
            toggle={() => setConfirmationModal(!confirmationModal)}
            className="subuser-modal-header"
          >
            Confirm Bulk Send
          </ModalHeader>
          <ModalBody className="p-3 p-sm-4">
            <div className="mb-3">
              <p className="contact-sub-text fs-13px mb-1">
                <span className="contact-name-text fw-semibold">Ticket:</span> {selectedTicket?.label}
              </p>
              <p className="contact-sub-text fs-13px mb-0">
                <span className="contact-name-text fw-semibold">Records:</span> {excelData.length}
              </p>
            </div>

            <div style={{ maxHeight: 360, overflow: "auto" }}>
              <Table className="mb-0" responsive>
                <thead>
                  <tr>
                    <th className="subuser-stat-label">Name</th>
                    <th className="subuser-stat-label">Email</th>
                    <th className="subuser-stat-label">Phone</th>
                    <th className="subuser-stat-label">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {excelData.map((row, index) => (
                    <tr key={index}>
                      <td className="contact-name-text fs-12px">
                        {row.firstName} {row.lastName}
                      </td>
                      <td className="contact-sub-text fs-12px">{row.emailId}</td>
                      <td className="contact-sub-text fs-12px">{row.phoneNumber}</td>
                      <td className="contact-sub-text fs-12px">{row.maxCount || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </ModalBody>
          <ModalFooter className="subuser-modal-footer gap-2">
            <button
              type="button"
              className="subuser-btn-secondary"
              onClick={() => setConfirmationModal(false)}
            >
              Cancel
            </button>
            <button type="button" className="subuser-btn-primary" onClick={handleSendBookings}>
              Confirm &amp; Send
            </button>
          </ModalFooter>
        </Modal>
      </div>
  );
};

export default SellTickets;
